<div
  class="
    bet-preview-section
    tw-mt-[80px]
  "
>

  <div
    class="
      tw-relative
      tw-bottom-[-11px]
      tw-z-[-1]
      tw-capitalize
      tw-w-[137px]
      tw-bg-[#769ED1]
      tw-text-center
      tw-text-[18px]
      tw-text-[#FFFFFF]
      tw-rounded-[4px]
      tw-pt-[5px]
      tw-pb-[12px]
      tw-px-[5px]
    "
  >
    {{ 'ui.wager.bet.preview' | translate: 'bet preview' }}
  </div>

  <div class="tw-flex tw-justify-between tw-bg-[#557DAC] tw-rounded-[13px]">

    <!-- Left column: bet details + total + (save bet for non-dutch/abc) -->
    <div
      class="
        tw-flex tw-flex-col
        tw-w-3/4
        tw-border-r-[3px]
        tw-border-dotted
        tw-border-[#FDD92E7A]
        tw-pt-[17px]
        tw-pr-[15px]
        tw-pb-[17px]
        tw-pl-[17px]
      "
    >
      <!--
        Loop over every applicable error line emitted by
        `BetService.toDisplayBetErrorMessage`. Each entry is rendered
        as its own block so the user sees every reason the wager
        can't be submitted (mobile parity), instead of collapsing
        them into one prioritized line and hiding the balance / max /
        min hint the moment `cantBet` flips on.
      -->
      <!--
        No leg/runner picked yet: show one clear prompt instead of the
        confusing min-amount / "no valid wager" error lines. The
        original error block is kept below as the @else-if branch.
      -->
      @if (importData && !hasSelectedRunners) {
        <div
          class="
            error-message
            tw-bg-[#FFFFFF]
            tw-rounded-[4px]
            tw-mb-[10px]
            tw-py-[2px]
            tw-px-[10px]
            tw-flex
            tw-flex-col
            tw-gap-[2px]
          "
          data-cy="bet-preview-no-runners"
        >
          <p
            class="
              tw-text-[16px]
              tw-font-bold
              tw-text-[#000000]
              tw-leading-[1.3]
              tw-mb-0
              tw-break-words"
          >
            {{ 'No Runners are selected' | translate: 'No Runners are selected' }}
          </p>
        </div>
      }
      @else if (errorMessage.length > 0) {
        <div
          class="
            error-message
            tw-bg-[#FFFFFF]
            tw-rounded-[4px]
            tw-mb-[10px]
            tw-py-[2px]
            tw-px-[10px]
            tw-flex
            tw-flex-col
            tw-gap-[2px]
          "
          data-cy="bet-preview-error"
        >
          @for (line of errorMessage; track line; let last = $last) {
            <p
              class="
                tw-text-[16px]
                tw-font-bold
                tw-text-[#000000]
                tw-leading-[1.3]
                tw-break-words"
              [class.tw-mb-0]="last"
              data-cy="bet-preview-error-line"
            >
              {{ line }}
            </p>
          }
        </div>
      }

      <!-- Mobile-style summary line: $amount POOL MODIFIER on RUNNERS R# -->
      <p
        class="
          bet-details
          tw-text-[18px] tw-font-bold tw-text-[#FFFFFF]
          tw-mb-[12px]
          tw-leading-[1.3]
          tw-break-words
        "
        data-cy="bet-preview-details"
      >
        <!-- Amount -->
        <span data-cy="bet-preview-amount">
          @if (
            selectedModifier?.toLowerCase() === 'dutchcost' &&
            importData?.dutchTicket
          ) {
            ${{ importData.dutchTicket.calcAmount() | number:'1.2-2' }}
          } @else if (selectedModifier?.toLowerCase() === 'abc') {
            ${{ (importData?.abcTotalAmount || 0) | number:'1.2-2' }}
          } @else {
            ${{ (selectedAmount || 0) | number:'1.2-2' }}
          }
        </span>

        <!-- Pool -->
        <span data-cy="bet-preview-pool" class="tw-ml-[2px]">
          {{ selectedPool?.PoolId || '' }}
        </span>

        <!-- Modifier (DutchCost is shown as "DUTCH") -->
        <span data-cy="bet-preview-modifier" class="tw-ml-[2px]">
          @if (selectedModifier === 'DutchCost') {
            {{ 'account.statements.dutch' | translate: 'DUTCH' }}
          } @else {
            {{ selectedModifier || '' }}
          }
        </span>

        <!-- Selected runners (e.g. 1,2/3-6). Falls back to the
             payload's copy when the @Input wasn't wired by the
             host (the runner.component always stamps runners onto
             the showBet payload before emitting it). -->
        @if (storedSelectedRunners || importData?.storedSelectedRunners) {
          <span class="tw-ml-[4px]">on</span>
          <span
            data-cy="bet-preview-runners"
            class="
              tw-ml-[4px]
              tw-text-[#FDD92E]
              tw-font-bold
              tw-tracking-wide
              tw-break-all
            "
          >
            {{ storedSelectedRunners || importData?.storedSelectedRunners }}
          </span>
        }

        <!-- Race -->
        @if (selectedRace?.RaceId !== undefined && selectedRace?.RaceId !== null) {
          <span data-cy="bet-preview-race" class="tw-ml-[6px]">
            R{{ selectedRace.RaceId }}
          </span>
        }
      </p>

      <!-- Total + Save Bet (save bet only for non-dutch/non-abc) -->
      <div class="tw-flex tw-justify-between tw-items-center tw-gap-[10px] tw-mt-auto">
        <div
          class="
            bet-total
            tw-flex-1
            tw-bg-[#000000]
            tw-text-[16px]
            tw-font-bold
            tw-text-[#FFFFFF]
            tw-rounded-[4px]
            tw-py-[5px]
            tw-px-[10px]
          "
          data-cy="bet-preview-total"
        >
          <span class="tw-text-[#FDD92E]">{{ 'generic.total' | translate: 'Total:' }}</span> ${{ totalAmount }}
        </div>

        @if (
          selectedModifier?.toLowerCase() !== 'dutchcost' &&
          selectedModifier?.toLowerCase() !== 'abc'
        ) {
          <button
            type="button"
            (click)="confirmBet('save-bet')"
            [disabled]="isProcessingBet || importData?.cantBet"
            class="
              save-bet
              tw-w-[106px]
              tw-capitalize
              tw-bg-[#036E23]
              hover:tw-bg-[#02551b]
              disabled:tw-opacity-50 disabled:tw-cursor-not-allowed
              tw-text-[16px]
              tw-text-[#FFFFFF]
              tw-rounded-[4px]
              tw-py-[5px]
              tw-px-[20px]
              tw-transition
            "
            data-cy="bet-preview-save-bet"
          >
            {{ 'wager.stackbet' | translate: 'save bet' }}
          </button>
        }
      </div>
    </div>

    <!-- Right column: hint + Submit/View Bets button -->
    <div
      class="
        tw-flex tw-flex-col tw-justify-between
        tw-w-1/4
        tw-pt-[17px] tw-pr-[17px] tw-pb-[17px] tw-pl-[14px]
      "
    >
      <p class="tw-text-[14px] tw-text-[#FFFFFF] tw-mb-[15px]">
        <img
          src="/assets/images/wager/wager_ticket_icon.png"
          class="tw-mb-[5px]"
          alt="{{ 'ui.wager.wager.ticket.icon' | translate: 'wager ticket icon' }}"
        >
        @if (importData) {
          @if (
            requireConfirmation &&
            (
              selectedModifier?.toLowerCase() === 'dutchcost' ||
              selectedModifier?.toLowerCase() === 'abc'
            )
          ) {
            {{
              'ui.wager.tap.view.bets.hint'
                | translate: '' : {
                    label: ('wager.viewbets' | translate: 'View Bets'),
                    type: (selectedModifier === 'DutchCost' ? 'dutch' : 'ABC')
                  }
            }}
          } @else {
            {{ 'ui.wager.review.your.selection.on.the.left' | translate: 'Review your selection on the left, then place or save your bet.' }}
          }
        } @else {
          {{ 'ui.wager.there.are.no.bets.placed.yet' | translate: 'There are no bets placed yet. Make your bets to see them here.' }}
        }
      </p>

      <button
        type="button"
        (click)="confirmBet('bet-now')"
        [disabled]="
          isProcessingBet ||
          importData?.cantBet ||
          (
            selectedModifier?.toLowerCase() === 'dutchcost' &&
            !importData?.dutchTotalAmount
          ) ||
          (
            selectedModifier?.toLowerCase() === 'abc' &&
            !importData?.abcTotalAmount
          )
        "
        class="
          submit-bet
          tw-w-full
          tw-text-center
          tw-capitalize
          tw-self-center
          tw-bg-[#FF6F6F]
          hover:tw-bg-[#e85959]
          disabled:tw-opacity-50 disabled:tw-cursor-not-allowed
          tw-text-[16px]
          tw-text-[#FFFFFF]
          tw-rounded-[4px]
          tw-mt-auto
          tw-py-[8px]
          tw-px-[10px]
          tw-transition
        "
        data-cy="bet-preview-submit-bet"
      >
        @if (
          requireConfirmation &&
          (
            selectedModifier?.toLowerCase() === 'dutchcost' ||
            selectedModifier?.toLowerCase() === 'abc'
          )
        ) {
          {{ 'wager.viewbets' | translate: 'View Bets' }}
        } @else {
          {{ 'wager.placebet' | translate: 'Submit Bet' }}
        }
      </button>
    </div>

  </div>
</div>
